/**
 * Wizard URL-search coercers — shared by the create-wizard route parsers
 * (vozidla nabídka, ojetá nabídka/smlouva/výkup, leasing).
 *
 * TanStack Router's `validateSearch` receives raw `Record<string, unknown>`
 * values: already-decoded `number`/`boolean` for app-driven navigations, but
 * plain strings for hand-typed or shared URLs — every coercer accepts both
 * shapes. Each wizard module keeps its own STEP_ORDER / titles / search
 * *type* and parse function (domain); only the value coercion lives here so
 * the parsers cannot drift.
 */

/** Finite number, or an all-digits string → its numeric value. */
export function coerceInt(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return undefined;
}

/** `coerceInt` restricted to > 0 — record-id params where 0 means "unset". */
export function coercePositiveInt(value: unknown): number | undefined {
  const n = coerceInt(value);
  return n != null && n > 0 ? n : undefined;
}

/** `true`/`false` booleans, or their `'true'|'1'` / `'false'|'0'` strings. */
export function coerceBool(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return undefined;
}

/**
 * Type-guard a raw `step` param against a module's step order — generic so
 * the narrowed type is the module's step-id union.
 */
export function isStepId<S extends string>(
  order: readonly S[],
  value: unknown,
): value is S {
  return typeof value === 'string' && (order as readonly string[]).includes(value);
}
